import "@/App.css";
import { useEffect } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingChatbot from "@/components/FloatingChatbot";
import ScrollToTop from "@/components/ScrollToTop";
import ScrollProgressBar from "@/components/ScrollProgressBar";
import CookieBanner from "@/components/CookieBanner";
import TopBar from "@/components/TopBar";
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import ServiceDetailPageV2 from "@/pages/ServiceDetailPageV2";
import { SERVICES_CONTENT } from "@/data/servicesContent";
import QuotePage from "@/pages/QuotePage";
import AboutPage from "@/pages/AboutPage";
import FaqPage from "@/pages/FaqPage";
import PartnersPage from "@/pages/PartnersPage";
import ContactPage from "@/pages/ContactPage";
import BlogIndexPage from "@/pages/BlogIndexPage";
import BlogPostPage from "@/pages/BlogPostPage";
import LegalNoticePage from "@/pages/LegalNoticePage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverviewPage from "@/pages/admin/AdminOverviewPage";
import AdminQuotesPage from "@/pages/admin/AdminQuotesPage";
import AdminQuoteDetailPage from "@/pages/admin/AdminQuoteDetailPage";
import AdminContactsPage from "@/pages/admin/AdminContactsPage";
import AdminContactDetailPage from "@/pages/admin/AdminContactDetailPage";
import AdminChatbotPage from "@/pages/admin/AdminChatbotPage";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";
import AdminNotificationsPage from "@/pages/admin/AdminNotificationsPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import NotFoundPage from "@/pages/NotFoundPage";

const ServiceRouter = () => {
  const { slug } = useParams();
  return SERVICES_CONTENT[slug] ? <ServiceDetailPageV2 /> : <ServiceDetailPage />;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        className="flex-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15, ease: "easeInOut" }}
      >
        <Routes location={location}>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:slug" element={<ServiceRouter />} />
          <Route path="/devis" element={<QuotePage />} />
          <Route path="/quote" element={<QuotePage />} />
          <Route path="/a-propos" element={<AboutPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/partenaires" element={<PartnersPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/mentions-legales" element={<LegalNoticePage />} />
          <Route path="/politique-confidentialite" element={<PrivacyPage />} />
          <Route path="/cgv" element={<TermsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
};

const Shell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  useEffect(() => {
    const handleClick = (e) => {
      const btn = e.target.closest(".btn-primary");
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 2;
      const ripple = document.createElement("span");
      ripple.style.cssText = `position:absolute;border-radius:50%;background:rgba(255,255,255,0.35);width:${size}px;height:${size}px;left:${e.clientX - rect.left - size / 2}px;top:${e.clientY - rect.top - size / 2}px;transform:scale(0);animation:ripple-btn 0.55s ease-out forwards;pointer-events:none;z-index:10;`;
      btn.appendChild(ripple);
      setTimeout(() => ripple.remove(), 600);
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  if (isAdmin) {
    return (
      <ErrorBoundary>
        <Routes>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverviewPage />} />
            <Route path="devis" element={<AdminQuotesPage />} />
            <Route path="devis/:id" element={<AdminQuoteDetailPage />} />
            <Route path="contacts" element={<AdminContactsPage />} />
            <Route path="contacts/:id" element={<AdminContactDetailPage />} />
            <Route path="chatbot" element={<AdminChatbotPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
            <Route path="notifications" element={<AdminNotificationsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Route>
        </Routes>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="App min-h-screen flex flex-col">
        <ScrollProgressBar />
        <TopBar />
        <Header />
        <AnimatedRoutes />
        <Footer />
        <FloatingChatbot />
        <CookieBanner />
      </div>
    </ErrorBoundary>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Shell />
    </BrowserRouter>
  );
}

export default App;
